import { useEffect, useState } from "react";
import { Inline } from "@/lib/inline";

// The independent professional's session console: a terminal-style transcript rather than chat bubbles.

export const COMMANDS = [
  ["/help", "list the commands"],
  ["/submit", "submit for review"],
  ["/propose", "propose text to shared memory"],
  ["/cap", "set this session's cap"],
  ["/stop", "stop the running turn (or press Esc)"],
  ["/lock", "lock the session"],
  ["/end", "end the session"],
  ["/clear", "clear the screen"],
];

// Tool names engines report, as a verb and a noun: "read 2 files", "ran 1 shell command".
const TOOL_WORDS = {
  Bash: ["ran", "shell command", "shell commands"],
  Read: ["read", "file", "files"],
  Edit: ["edited", "file", "files"],
  MultiEdit: ["edited", "file", "files"],
  Write: ["wrote", "file", "files"],
  NotebookEdit: ["edited", "notebook", "notebooks"],
  Grep: ["searched for", "pattern", "patterns"],
  Glob: ["searched for", "pattern", "patterns"],
  WebSearch: ["searched the web", "time", "times"],
  WebFetch: ["fetched", "page", "pages"],
  Task: ["ran", "subagent", "subagents"],
  Agent: ["ran", "subagent", "subagents"],
  TodoWrite: ["updated", "plan", "plans"],
  MCP: ["called", "tool", "tools"],
};

export function toolSummary(counts) {
  const parts = new Map();
  for (const [tool, n] of Object.entries(counts)) {
    const [verb, one, many] = TOOL_WORDS[tool] || [`used ${tool}`, "time", "times"];
    const key = `${verb}|${one}|${many}`;
    parts.set(key, (parts.get(key) || 0) + n);
  }
  const text = [...parts].map(([key, n]) => {
    const [verb, one, many] = key.split("|");
    return `${verb} ${n} ${n === 1 ? one : many}`;
  }).join(", ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// Engines separate text blocks with blank lines; each block is a line of its own here.
export const tidy = (text) => String(text || "").replace(/^(\s*\n)+/, "").trimEnd();

export function duration(ms) {
  const s = Math.max(1, Math.round(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

const SPINNER = ["·", "✢", "✳", "✶", "✻", "✽", "✻", "✶", "✳", "✢"];

export function WorkingLine({ since, tool }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 120); return () => clearInterval(t); }, []);
  const frame = SPINNER[Math.floor(now / 120) % SPINNER.length];
  return (
    <div className="flex gap-2.5 text-muted-foreground" data-testid="terminal-working">
      <span className="w-3 text-center select-none text-gr-warning">{frame}</span>
      <span>Working… ({duration(now - since)}{tool ? ` · ${tool}` : ""})</span>
    </div>
  );
}

// One transcript line, as the handoff's terminal draws them. Kinds: user, assistant, tools, file, commit, error, info, done.
// `onOpenFile`: a file line opens that file in the diff viewer (DiffViewer.jsx).
export function Entry({ e, index, onOpenFile }) {
  switch (e.kind) {
    case "user":
      return (
        <div className="flex gap-2.5 mt-2.5" data-testid={`msg-user-${index}`}>
          <span className="text-muted-foreground select-none">❯</span>
          <span className="min-w-0 flex flex-col gap-1.5">
            <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{e.text}</span>
            {e.images && (  // pasted screenshots: held in memory while the session is open
              <span className="flex flex-wrap gap-2">
                {e.images.map((img, i) => (img.url
                  ? <a key={i} href={img.url} target="_blank" rel="noreferrer"><img src={img.url} alt={`Screenshot ${i + 1}`} className="max-h-40 max-w-[240px] rounded border" /></a>
                  : <span key={i} className="h-16 w-24 rounded border bg-gr-surface-2" aria-label="Screenshot loading" />))}
              </span>
            )}
          </span>
        </div>
      );
    case "assistant":
      return (
        <div className="flex gap-2.5" data-testid={`msg-assistant-${index}`}>
          <span className="select-none">●</span>
          <span className="whitespace-pre-wrap [overflow-wrap:anywhere] min-w-0 text-gr-ink-secondary"><Inline text={e.text} /></span>
        </div>
      );
    case "tools":
      return <div className="pl-[22px] text-muted-foreground">⎿ {toolSummary(e.counts)}</div>;
    case "file": {
      const line = <><span className="text-foreground">{e.path}</span><span className="text-gr-success"> +{e.added}</span><span className="text-gr-danger"> −{e.removed}</span></>;
      return onOpenFile ? (
        <button type="button" onClick={() => onOpenFile(e.path)} title="See this file's changes" data-testid={`terminal-file-${index}`}
          className="pl-[22px] text-left text-muted-foreground [overflow-wrap:anywhere] hover:underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
          {line}
        </button>
      ) : <div className="pl-[22px] text-muted-foreground [overflow-wrap:anywhere]">{line}</div>;
    }
    case "commit":
      return (
        <div className="pl-[22px] text-muted-foreground [overflow-wrap:anywhere]">
          Committed <span className="text-gr-warning">{(e.sha || "").slice(0, 7)}</span> · {e.message}
        </div>
      );
    case "error":
      return <div className="text-gr-danger whitespace-pre-wrap [overflow-wrap:anywhere]" data-testid={`msg-error-${index}`}>{e.text}</div>;
    case "done":
      return <div className="text-muted-foreground">✓ {e.text}</div>;
    default:
      return <div className="text-muted-foreground whitespace-pre-wrap">{e.text}</div>;
  }
}
